import { Directive, ElementRef, NgZone, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { MatLegacyFormField as MatFormField } from '@angular/material/legacy-form-field';
import { MatLegacySelect as MatSelect } from '@angular/material/legacy-select';
import { Subscription } from 'rxjs';

import { AppLayoutService } from '../../services/app-layout.service';

@Directive({
  selector: 'mat-select',
})
export class SelectSheetDirective implements OnInit, OnDestroy {
  private openedSub = Subscription.EMPTY;
  private layoutSub = Subscription.EMPTY;
  private nativeClose: (() => void) | null = null;
  private nativeUpdatePosition: (() => void) | null = null;
  private nativeUpdateSize: ((size: unknown) => void) | null = null;
  private styleObserver: MutationObserver | null = null;
  private pinRaf = 0;
  private decoratedPane: HTMLElement | null = null;
  private decoratedSheet: HTMLElement | null = null;
  private paneHome: { parent: Node; next: ChildNode | null } | null = null;

  constructor(
    @Self() private select: MatSelect,
    private host: ElementRef<HTMLElement>,
    @Optional() private formField: MatFormField,
    private appLayout: AppLayoutService,
    private ngZone: NgZone,
  ) {}

  ngOnInit() {
    this.syncOptionCentering();
    this.layoutSub = this.appLayout.layoutChanges.subscribe(() => this.syncOptionCentering());

    this.openedSub = this.select.openedChange.subscribe(open => {
      if (open && this.shouldUseSheet) {
        this.installCloseGuard();
        this.scheduleDecorate(0);
        return;
      }

      this.teardownSheet();
    });
  }

  ngOnDestroy() {
    this.openedSub.unsubscribe();
    this.layoutSub.unsubscribe();
    this.teardownSheet();
    this.restoreClose();
  }

  private get shouldUseSheet(): boolean {
    return this.appLayout.isMobile;
  }

  private syncOptionCentering() {
    this.select.disableOptionCentering = this.appLayout.isMobile;
  }

  private scheduleDecorate(attempt: number) {
    const panel = this.panelElement;
    if (panel) {
      this.decoratePanel(panel);
      return;
    }

    if (attempt < 8) {
      setTimeout(() => this.scheduleDecorate(attempt + 1), 0);
    }
  }

  private decoratePanel(panel: HTMLElement) {
    const pane = panel.closest('.cdk-overlay-pane') as HTMLElement;
    if (!pane) {
      return;
    }

    this.decoratedPane = pane;
    pane.classList.add('jct-select-overlay', 'jct-select-overlay--mobile');
    panel.classList.add('jct-select-sheet-panel');

    const wrap = panel.parentElement;
    const listHost = wrap && wrap.classList.contains('mat-select-panel-wrap') ? wrap : panel;

    if (wrap && wrap.classList.contains('mat-select-panel-wrap')) {
      wrap.classList.add('jct-select-sheet-wrap');
    }

    let sheet = pane.querySelector(':scope > .jct-select-sheet') as HTMLElement;
    if (!sheet) {
      sheet = this.buildSheet();
      pane.appendChild(sheet);
    }

    const body = sheet.querySelector('.jct-select-sheet-body') as HTMLElement;
    if (body && listHost.parentElement !== body) {
      body.appendChild(listHost);
    }

    this.decoratedSheet = sheet;
    this.refreshTitle(sheet);
    this.hookOverlayWrites(pane);
    this.watchOverlayStyles(pane);
    this.reapplySheetLayout(pane);

    if (this.pinRaf) {
      cancelAnimationFrame(this.pinRaf);
    }

    this.pinRaf = requestAnimationFrame(() => {
      this.pinRaf = 0;
      if (!this.select.panelOpen || !pane.isConnected) {
        return;
      }

      this.reapplySheetLayout(pane);
      this.markBackdrop(pane);
    });

    this.markBackdrop(pane);
  }

  private buildSheet(): HTMLElement {
    const sheet = document.createElement('div');
    sheet.className = 'jct-select-sheet';
    sheet.setAttribute('role', 'presentation');

    const header = this.buildHeader();
    const dividerTop = this.buildDivider();
    const body = document.createElement('div');
    body.className = 'jct-select-sheet-body';
    const dividerBottom = this.buildDivider();
    const footer = this.buildFooter();

    sheet.appendChild(header);
    sheet.appendChild(dividerTop);
    sheet.appendChild(body);
    sheet.appendChild(dividerBottom);
    sheet.appendChild(footer);

    return sheet;
  }

  private rehomePane(pane: HTMLElement) {
    const container = pane.closest('.cdk-overlay-container') as HTMLElement;
    if (!container || pane.parentElement === container) {
      return;
    }

    if (!this.paneHome && pane.parentElement) {
      this.paneHome = { parent: pane.parentElement, next: pane.nextSibling };
    }

    container.appendChild(pane);
  }

  private restorePaneHome(pane: HTMLElement) {
    if (!this.paneHome) {
      return;
    }

    const { parent, next } = this.paneHome;
    this.paneHome = null;

    if (!parent.isConnected || !pane.isConnected) {
      return;
    }

    parent.insertBefore(pane, next);
  }

  private sheetHeightPx() {
    return Math.max(240, window.innerHeight - 65);
  }

  private reapplySheetLayout(pane: HTMLElement) {
    const sheet =
      this.decoratedSheet || (pane.querySelector(':scope > .jct-select-sheet') as HTMLElement);
    if (sheet) {
      this.decoratedSheet = sheet;
      const body = sheet.querySelector('.jct-select-sheet-body') as HTMLElement;
      const wrap = pane.querySelector(
        '.mat-select-panel-wrap, .jct-select-sheet-wrap',
      ) as HTMLElement;
      if (body && wrap && wrap.parentElement !== body) {
        body.appendChild(wrap);
      }
    }

    this.rehomePane(pane);
    this.pinBoundingBox(pane);
    this.pinOverlay(pane);
    this.releasePanelScroll(pane);
  }

  private pinBoundingBox(pane: HTMLElement) {
    const box = pane.closest('.cdk-overlay-connected-position-bounding-box') as HTMLElement;
    if (!box) {
      return;
    }

    box.classList.add('jct-select-overlay-box');
    box.style.setProperty('position', 'fixed', 'important');
    box.style.setProperty('top', '0px', 'important');
    box.style.setProperty('left', '0px', 'important');
    box.style.setProperty('right', '0px', 'important');
    box.style.setProperty('bottom', '0px', 'important');
    box.style.setProperty('width', '100%', 'important');
    box.style.setProperty('height', '100%', 'important');
    box.style.setProperty('max-width', 'none', 'important');
    box.style.setProperty('max-height', 'none', 'important');
    box.style.setProperty('display', 'flex', 'important');
    box.style.setProperty('flex-direction', 'column', 'important');
    box.style.setProperty('align-items', 'stretch', 'important');
    box.style.setProperty('justify-content', 'flex-end', 'important');
    box.style.setProperty('pointer-events', 'none', 'important');
  }

  private restoreBoundingBox(pane: HTMLElement) {
    const box = pane.closest('.cdk-overlay-connected-position-bounding-box') as HTMLElement;
    if (!box) {
      return;
    }

    box.classList.remove('jct-select-overlay-box');
    [
      'position',
      'top',
      'left',
      'right',
      'bottom',
      'width',
      'height',
      'max-width',
      'max-height',
      'display',
      'flex-direction',
      'align-items',
      'justify-content',
      'pointer-events',
    ].forEach(prop => box.style.removeProperty(prop));
  }

  private releasePanelScroll(pane: HTMLElement) {
    const panel = pane.querySelector('.mat-select-panel') as HTMLElement;
    if (!panel) {
      return;
    }

    panel.style.setProperty('max-height', 'none', 'important');
    panel.style.setProperty('height', 'auto', 'important');
    panel.style.setProperty('overflow', 'visible', 'important');
    panel.style.setProperty('transform', 'none', 'important');
  }

  private pinOverlay(pane: HTMLElement) {
    const observer = this.styleObserver;
    if (observer) {
      observer.disconnect();
    }

    const height = `${this.sheetHeightPx()}px`;

    pane.style.setProperty('position', 'fixed', 'important');
    pane.style.setProperty('display', 'flex', 'important');
    pane.style.setProperty('flex-direction', 'column', 'important');
    pane.style.setProperty('top', 'auto', 'important');
    pane.style.setProperty('bottom', '0px', 'important');
    pane.style.setProperty('left', '0px', 'important');
    pane.style.setProperty('right', '0px', 'important');
    pane.style.setProperty('width', '100%', 'important');
    pane.style.setProperty('max-width', '100vw', 'important');
    pane.style.setProperty('min-width', '100%', 'important');
    pane.style.setProperty('height', height, 'important');
    pane.style.setProperty('max-height', height, 'important');
    pane.style.setProperty('min-height', height, 'important');
    pane.style.setProperty('overflow', 'hidden', 'important');
    pane.style.setProperty('transform', 'none', 'important');
    pane.style.setProperty('pointer-events', 'auto', 'important');

    if (observer && this.select.panelOpen && pane.isConnected) {
      this.observeOverlayStyles(pane, observer);
    }
  }

  private hookOverlayWrites(pane: HTMLElement) {
    const overlayRef = this.overlayRef;
    if (!overlayRef || this.nativeUpdatePosition) {
      return;
    }

    this.nativeUpdatePosition = overlayRef.updatePosition.bind(overlayRef);
    overlayRef.updatePosition = () => {
      if (this.nativeUpdatePosition) {
        this.nativeUpdatePosition();
      }
      if (this.select.panelOpen && pane.isConnected) {
        this.reapplySheetLayout(pane);
      }
    };

    if (overlayRef.updateSize) {
      this.nativeUpdateSize = overlayRef.updateSize.bind(overlayRef);
      overlayRef.updateSize = (size: unknown) => {
        if (this.nativeUpdateSize) {
          this.nativeUpdateSize(size);
        }
        if (this.select.panelOpen && pane.isConnected) {
          this.reapplySheetLayout(pane);
        }
      };
    }
  }

  private restoreOverlayWrites() {
    const overlayRef = this.overlayRef;
    if (overlayRef && this.nativeUpdatePosition) {
      overlayRef.updatePosition = this.nativeUpdatePosition;
    }
    if (overlayRef && this.nativeUpdateSize) {
      overlayRef.updateSize = this.nativeUpdateSize;
    }
    this.nativeUpdatePosition = null;
    this.nativeUpdateSize = null;
  }

  private watchOverlayStyles(pane: HTMLElement) {
    this.disconnectStyleObserver();

    this.styleObserver = new MutationObserver(() => {
      if (this.select.panelOpen && pane.isConnected) {
        this.reapplySheetLayout(pane);
      }
    });

    this.observeOverlayStyles(pane, this.styleObserver);
  }

  private observeOverlayStyles(pane: HTMLElement, observer: MutationObserver) {
    this.ngZone.runOutsideAngular(() => {
      observer.observe(pane, { attributes: true, attributeFilter: ['style', 'class'] });
      const box = pane.closest('.cdk-overlay-connected-position-bounding-box');
      if (box) {
        observer.observe(box, { attributes: true, attributeFilter: ['style', 'class'] });
      }
    });
  }

  private disconnectStyleObserver() {
    if (this.styleObserver) {
      this.styleObserver.disconnect();
      this.styleObserver = null;
    }
  }

  private teardownSheet() {
    if (this.pinRaf) {
      cancelAnimationFrame(this.pinRaf);
      this.pinRaf = 0;
    }

    this.disconnectStyleObserver();
    this.restoreOverlayWrites();

    const pane =
      this.decoratedPane && this.decoratedPane.isConnected
        ? this.decoratedPane
        : (document.querySelector('.cdk-overlay-pane.jct-select-overlay--mobile') as HTMLElement);

    this.decoratedPane = null;
    this.decoratedSheet = null;

    if (!pane) {
      this.paneHome = null;
      return;
    }

    this.restoreBoundingBox(pane);

    const panel = pane.querySelector('.mat-select-panel') as HTMLElement;
    if (panel) {
      ['max-height', 'height', 'overflow', 'transform'].forEach(prop =>
        panel.style.removeProperty(prop),
      );
    }

    const wrap = pane.querySelector(
      '.mat-select-panel-wrap, .jct-select-sheet-wrap',
    ) as HTMLElement;
    const sheet = pane.querySelector(':scope > .jct-select-sheet') as HTMLElement;
    if (wrap && wrap.parentElement !== pane) {
      pane.insertBefore(wrap, sheet || null);
    }
    if (sheet) {
      sheet.remove();
    }

    this.restorePaneHome(pane);

    pane.classList.remove('jct-select-overlay', 'jct-select-overlay--mobile');
    [
      'position',
      'display',
      'flex-direction',
      'height',
      'min-height',
      'top',
      'bottom',
      'left',
      'right',
      'width',
      'max-width',
      'min-width',
      'max-height',
      'overflow',
      'overflow-x',
      'overflow-y',
      'transform',
    ].forEach(prop => pane.style.removeProperty(prop));

    const container = pane.closest('.cdk-overlay-container') || document;
    const backdrop = container.querySelector('.jct-select-overlay-backdrop');
    if (backdrop) {
      backdrop.classList.remove('jct-select-overlay-backdrop');
      backdrop.classList.add('cdk-overlay-transparent-backdrop');
    }
  }

  private markBackdrop(pane: HTMLElement) {
    const container = pane.closest('.cdk-overlay-container') || pane.parentElement;
    const backdrop = container && container.querySelector('.cdk-overlay-backdrop');

    if (!backdrop) {
      return;
    }

    backdrop.classList.remove('cdk-overlay-transparent-backdrop');
    backdrop.classList.add('jct-select-overlay-backdrop');
  }

  private installCloseGuard() {
    if (this.nativeClose) {
      return;
    }

    const select = this.select as MatSelect & { close: () => void };
    this.nativeClose = select.close.bind(select);
    select.close = () => this.guardedClose();
  }

  private guardedClose() {
    this.teardownSheet();
    if (this.nativeClose) {
      this.nativeClose();
    }
  }

  private dismiss() {
    this.ngZone.run(() => {
      this.teardownSheet();
      if (this.nativeClose) {
        this.nativeClose();
      } else {
        this.select.close();
      }
      this.swallowGhostClick();
    });
  }

  private swallowGhostClick() {
    const swallow = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      document.removeEventListener('click', swallow, true);
      document.removeEventListener('pointerup', swallow, true);
    };

    document.addEventListener('click', swallow, true);
    document.addEventListener('pointerup', swallow, true);
    setTimeout(() => {
      document.removeEventListener('click', swallow, true);
      document.removeEventListener('pointerup', swallow, true);
    }, 400);
  }

  private restoreClose() {
    if (!this.nativeClose) {
      return;
    }

    (this.select as MatSelect & { close: () => void }).close = this.nativeClose;
    this.nativeClose = null;
  }

  private buildHeader(): HTMLElement {
    const header = document.createElement('div');
    header.className = 'jct-select-sheet-header';
    header.setAttribute('role', 'presentation');

    const handle = document.createElement('div');
    handle.className = 'jct-select-sheet-handle';
    handle.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      this.dismiss();
    });
    header.appendChild(handle);

    const title = document.createElement('div');
    title.className = 'jct-select-sheet-title';
    title.textContent = this.resolveTitle();
    header.appendChild(title);

    return header;
  }

  private refreshTitle(root: HTMLElement) {
    const title = root.querySelector('.jct-select-sheet-title') as HTMLElement;
    if (title) {
      title.textContent = this.resolveTitle();
    }
  }

  private buildDivider(): HTMLElement {
    const divider = document.createElement('div');
    divider.className = 'jct-select-sheet-divider';
    divider.setAttribute('role', 'separator');
    return divider;
  }

  private buildFooter(): HTMLElement {
    const footer = document.createElement('div');
    footer.className = 'jct-select-sheet-footer';
    footer.setAttribute('role', 'presentation');

    const clear = document.createElement('button');
    clear.type = 'button';
    clear.className = 'jct-select-sheet-clear';
    clear.textContent = $localize`נקה`;
    clear.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      this.ngZone.run(() => {
        this.select.value = this.select.multiple ? [] : null;
      });
      this.dismiss();
    });

    const confirm = document.createElement('button');
    confirm.type = 'button';
    confirm.className = 'jct-select-sheet-confirm';
    confirm.textContent = $localize`אישור`;
    confirm.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      this.dismiss();
    });

    footer.appendChild(clear);
    footer.appendChild(confirm);
    return footer;
  }

  private resolveTitle(): string {
    const fieldEl = this.formFieldElement;

    const fromMatLabel = this.readableText(fieldEl && fieldEl.querySelector('mat-label'));
    if (fromMatLabel) {
      return fromMatLabel;
    }

    const fromFloatingLabel = this.readableText(
      fieldEl && fieldEl.querySelector('.mat-form-field-label'),
    );
    if (fromFloatingLabel) {
      return fromFloatingLabel;
    }

    const labelledBy = this.host.nativeElement.getAttribute('aria-labelledby');
    if (labelledBy) {
      const fromAria = this.readableText(document.getElementById(labelledBy));
      if (fromAria) {
        return fromAria;
      }
    }

    const ariaLabel = this.host.nativeElement.getAttribute('aria-label');
    if (ariaLabel && ariaLabel.trim()) {
      return ariaLabel.trim();
    }

    if (this.select.placeholder) {
      return this.select.placeholder.trim();
    }

    const fromColumn = this.columnHeaderText();
    if (fromColumn) {
      return fromColumn;
    }

    const fromPrompt = this.placeholderOptionText();
    if (fromPrompt) {
      return fromPrompt;
    }

    return '';
  }

  private get formFieldElement(): HTMLElement | null {
    const fromInjected =
      this.formField &&
      (this.formField as MatFormField & { _elementRef?: ElementRef<HTMLElement> });
    if (fromInjected && fromInjected._elementRef && fromInjected._elementRef.nativeElement) {
      return fromInjected._elementRef.nativeElement;
    }

    return this.host.nativeElement.closest('mat-form-field');
  }

  private readableText(el: Element | null): string {
    if (!el) {
      return '';
    }

    const clone = el.cloneNode(true) as HTMLElement;
    const noise = clone.querySelectorAll(
      '.mat-form-field-required-marker, .mat-placeholder-required, mat-error, mat-hint',
    );
    Array.prototype.forEach.call(
      noise,
      (node: Element) => node.parentNode && node.parentNode.removeChild(node),
    );

    return (clone.textContent || '').replace(/\s+/g, ' ').trim();
  }

  private columnHeaderText(): string {
    const cell = this.host.nativeElement.closest('td, .mat-cell') as HTMLTableCellElement;
    if (!cell) {
      return '';
    }

    const row = cell.parentElement as HTMLTableRowElement;
    const table = cell.closest('table');
    if (!row || !table || !row.cells) {
      return '';
    }

    const index = Array.prototype.indexOf.call(row.cells, cell);
    if (index < 0) {
      return '';
    }

    const headerRow = table.querySelector('tr.mat-header-row, thead tr');
    const headerCell = headerRow && (headerRow.children[index] as HTMLElement);
    return this.readableText(headerCell);
  }

  private placeholderOptionText(): string {
    const options = this.select.options;
    if (!options || !options.length) {
      return '';
    }

    const first = options.first;
    if (!first) {
      return '';
    }

    const value = first.value;
    if (value === 0 || value === '' || value == null) {
      return (first.viewValue || '').replace(/\s+/g, ' ').trim();
    }

    return '';
  }

  private get panelElement(): HTMLElement | null {
    const panelRef = (this.select as MatSelect & { panel?: ElementRef<HTMLElement> }).panel;
    if (panelRef && panelRef.nativeElement) {
      return panelRef.nativeElement;
    }

    return null;
  }

  private get overlayRef(): OverlayRefLike | null {
    const select = this.select as MatSelect & {
      _overlayDir?: { overlayRef?: OverlayRefLike };
    };
    return (select._overlayDir && select._overlayDir.overlayRef) || null;
  }
}

interface OverlayRefLike {
  updatePosition: () => void;
  updateSize?: (size: unknown) => void;
}
